import { SymbolLibrary } from '@components/ArtBoard/SettingsSidebar/components/SymbolLibrary'
import { Panel } from '@lib/components'
import { ThemeColors } from '@octopus-do/octopus-storybook'
import { artBoardActions, selectArtboardMode } from '@store/reducers/artboard'
import type { ArtBoardMode } from '@store/reducers/artboard/types'
import type React from 'react'
import { type UIEvent, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { useArtboardMode } from '@/hooks'
import { clsJoin } from '@/utils'
import {
  ActivityHistory,
  AttachedFiles,
  Colors,
  ProjectSettings,
  SettingsSwitcher,
} from './components'
import css from './SettingsSidebar.module.scss'

const ModeTitles: Partial<Record<ArtBoardMode, string>> = {
  settings: 'Project settings',
  color: 'Colors',
  symbol_library: 'Symbols',
  activity_history: 'Activity history',
  storage: 'Attached files',
}

export const SettingsSidebar: React.FC = () => {
  const [showBorder, setShowBorder] = useState(false)
  const [isScroll, setIsScroll] = useState(false)
  const contentRef = useRef<HTMLDivElement>(null)

  const dispatch = useDispatch()

  const mode = useSelector(selectArtboardMode)
  const { activityHistory, storage, symbolLibrary, settings, color } =
    useArtboardMode()

  const title = ModeTitles[mode]

  const handleSetIsScroll = () => {
    if (contentRef.current) {
      const { current } = contentRef

      if (current.clientHeight < current.scrollHeight) {
        setIsScroll(true)
      } else {
        setIsScroll(false)
      }
    }
  }

  useEffect(() => {
    if (contentRef.current) {
      handleSetIsScroll()

      window.addEventListener('resize', handleSetIsScroll)
    }

    return () => {
      window.removeEventListener('resize', handleSetIsScroll)
    }
  }, [contentRef])

  const handleOnClose = () => {
    dispatch(artBoardActions.changeMode('edit'))
  }

  const handleContentScroll = (e: UIEvent<HTMLDivElement>) => {
    if (e.currentTarget.scrollTop >= 2 && (activityHistory || storage)) {
      setShowBorder(true)
    } else {
      setShowBorder(false)
    }
  }

  const cnClassName = clsJoin(
    css.container,
    ThemeColors.light,
    symbolLibrary && css.symbolLibrary
  )

  const contentClassName = clsJoin(
    css.content,
    (activityHistory || storage) && css.withoutSwitcher,
    isScroll && css.withScroll
  )

  return (
    <Panel
      title={title}
      containerClassName={css.panelContainer}
      showHeaderBorder={showBorder}
      onClose={handleOnClose}
    >
      <div className={cnClassName}>
        {!activityHistory && !storage && <SettingsSwitcher mode={mode} />}

        <div
          ref={contentRef}
          className={contentClassName}
          onScroll={handleContentScroll}
        >
          {settings && <ProjectSettings />}

          {color && <Colors />}

          {symbolLibrary && <SymbolLibrary />}

          {activityHistory && <ActivityHistory />}

          {storage && <AttachedFiles />}
        </div>
      </div>
    </Panel>
  )
}
